import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Editor } from "@tinymce/tinymce-react";
// Import TinyMce
import tinymce from "tinymce";
// CSS
import "tinymce/themes/silver";
import "tinymce/skins/ui/oxide/skin.css";
// Default Icons
import "tinymce/icons/default";

// Plugins
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/advlist";
import { editNews, getDetailNews, getListNews } from "../../../../actions/news";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../Layout";
import Loading from "../../../../component/Loading";
import capitalize from "../../../../utils/Capitalize";
import { toastError, toastSuccess } from "../../../../component/Toast";

const EditNews = () => {
    const [picture, setPicture] = useState(null);
    const [imgData, setImgData] = useState(null);
    const [content, setContent] = useState();
    const [creator, setCreator] = useState("");
    const [oldImg, setOldImg] = useState("");
    const dispatch = useDispatch();
    const news = useSelector((state) => state.news);
    const params = useParams();
    const history = useHistory();

    const {
        handleSubmit,
        register,
        formState: { errors },
        setValue,
        control,
        reset,
        getValues,
        clearErrors,
        setError,
    } = useForm();

    useEffect(() => {
        dispatch(getDetailNews({ id: params.id }))
            .unwrap()
            .then((res) => {
                setValue("title", res.data.title);
                setValue("content", res.data.content);
                setContent(res.data.content);
                setCreator(res.data.user.name);
                setOldImg(res.data.image);
            });
    }, []);

    //handling image change
    const handleChange = (e) => {
        clearErrors("image");

        let allowedExtensions = /(\.jpg|\.jpeg|\.png|\.webp)$/i;
        let filePath = e.target.value;
        if (!allowedExtensions.exec(filePath)) {
            setError("image", {
                message: "Only JPEG, JPG, PNG & WEBP allowed!",
            });
            setImgData(null);
            return;
        }

        if (e.target?.files[0]?.size > 2000000) {
            setError("image", {
                message: "Maximum image size 2Mb",
            });
            setImgData(null);
            return;
        }

        setValue("image", e.target.files[0]); //manually set value image for form
        setPicture(e.target.files[0]);
        const reader = new FileReader();
        reader.addEventListener("load", () => {
            setImgData(reader.result); //set img data with url so it can showed after open
        });
        reader.readAsDataURL(e.target.files[0]);
    };

    //handling tinymce on change
    const handleEditorChange = (change) => {
        setContent(change);
        setValue("content", change);
        clearErrors("content");
    };

    const onSubmit = async (data) => {
        let fd = new FormData();
        fd.append("_method", "PUT");
        fd.append("image", data.image ? data.image : "");
        fd.append("title", data.title);
        fd.append("content", data.content);
        fd.append("approval_status", 2);

        // dispatch create news and store data
        dispatch(editNews({ id: params.id, data: fd }))
            .unwrap()
            .then((data) => {
                toastSuccess(capitalize(data.message), {
                    onClose: () => {
                        history.push("/admin/news");
                    },
                });
                reset();
                setImgData(null);
                dispatch(
                    getListNews(
                        news.newsList ? news.newsList?.meta?.current_page : 1
                    )
                );
            });
    };

    return (
        <Layout>
            <ToastContainer />
            <div className="container min-h-screen p-12">
                <h1 className="mb-5 text-xl font-bold">Edit News</h1>
                <div className="px-4 py-6 bg-gray-200 shadow-lg card">
                    <div className="flex items-center w-full gap-2 pb-3 mb-8 text-blue-900 border-b border-gray-400">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-6 h-6"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                            />
                        </svg>

                        <span className="text-sm font-semibold">
                            Creator : {creator}
                        </span>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className="w-11/12">
                        <div className="mb-6 md:w-1/2">
                            <label
                                htmlFor="division"
                                className="block mb-2 text-sm font-semibold text-blue-900"
                            >
                                Title
                            </label>
                            <input
                                type="text"
                                name="title-news"
                                id="title-news"
                                {...register("title", {
                                    required: "Title is required",
                                })}
                                className="w-full px-3 py-2 placeholder-gray-300 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 dark:border-gray-600 dark:focus:ring-gray-900 dark:focus:border-gray-500"
                            />
                            {errors?.title && (
                                <div className="text-xs font-semibold text-red-500">
                                    {errors.title.message}
                                </div>
                            )}
                        </div>
                        <div className="mb-6 md:w-1/2">
                            <label
                                htmlFor="image"
                                className="block mb-2 text-sm font-semibold text-blue-900"
                            >
                                News Image
                            </label>
                            <p className="mb-2 text-xs italic">
                                (Max Size: 2Mb; Extension: jpeg, jpg, png, webp)
                            </p>
                            <input
                                accept="image/webp, image/png, image/jpg, image/jpeg"
                                type="file"
                                // {...register("img")}
                                name="image"
                                id="image"
                                onChange={handleChange}
                                className="w-full px-3 py-2 placeholder-gray-300 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 dark:border-gray-600 dark:focus:ring-gray-900 dark:focus:border-gray-500"
                            />
                            {errors?.image && (
                                <div className="mt-2 text-xs font-semibold text-red-500">
                                    {errors.image.message}
                                </div>
                            )}
                        </div>
                        {picture ? (
                            <div className="mb-6">
                                <img src={imgData} alt={getValues.image} />
                            </div>
                        ) : (
                            <div className="mb-6">
                                <img src={oldImg} alt="news-banner" />
                            </div>
                        )}
                        <div className="w-full mb-6">
                            <label
                                htmlFor="content"
                                className="block mb-2 text-sm font-semibold text-blue-900"
                            >
                                Content
                            </label>
                            <Controller
                                name="content"
                                control={control}
                                defaultValue=""
                                render={({ onChange, value }) => (
                                    <Editor
                                        {...register("content", {
                                            required: "Please fill the content",
                                        })}
                                        cloudChannel="dev"
                                        name="content"
                                        init={{
                                            selector: "textarea",
                                            height: "480",
                                            skin: false,
                                            plugins:
                                                "link image textpattern lists advlist anchor",
                                            menubar:
                                                "file edit view insert format",
                                            toolbar:
                                                "undo redo | styleselect | bold italic | alignleft aligncenter alignright alignjustify | numlist bullist | outdent indent | link image",
                                            statusbar: false,
                                            /* enable title field in the Image dialog*/
                                            image_title: true,
                                            /* enable automatic uploads of images represented by blob or data URIs*/
                                            automatic_uploads: true,
                                            file_picker_types: "image",
                                            /* and here's our custom image picker*/

                                            file_picker_callback: function (
                                                cb,
                                                value,
                                                meta
                                            ) {
                                                let input =
                                                    document.createElement(
                                                        "input"
                                                    );
                                                input.setAttribute(
                                                    "type",
                                                    "file"
                                                );
                                                input.setAttribute("accept", [
                                                    "image/webp",
                                                    "image/png",
                                                    "image/jpg",
                                                    "image/jpeg",
                                                ]);

                                                input.onchange = function () {
                                                    let file = this.files[0];
                                                    clearErrors("content");

                                                    let filePath = this.value;
                                                    let allowedExtensions =
                                                        /(\.jpg|\.jpeg|\.png|\.webp)$/i;
                                                    if (
                                                        !allowedExtensions.exec(
                                                            filePath
                                                        )
                                                    ) {
                                                        setError("content", {
                                                            message:
                                                                "Only JPEG, JPG, PNG & WEBP allowed!",
                                                        });
                                                        toastError(
                                                            errors?.content &&
                                                                errors.content
                                                                    .message
                                                        );
                                                        return;
                                                    }

                                                    if (file.size > 2000000) {
                                                        setError("content", {
                                                            message:
                                                                "Maximum image size 2Mb",
                                                        });
                                                        toastError(
                                                            errors?.content &&
                                                                errors.content
                                                                    .message
                                                        );
                                                        return;
                                                    }

                                                    let reader =
                                                        new FileReader();
                                                    reader.onload =
                                                        function () {
                                                            let id =
                                                                "blobid" +
                                                                new Date().getTime();
                                                            let blobCache =
                                                                tinymce
                                                                    .activeEditor
                                                                    .editorUpload
                                                                    .blobCache;
                                                            let base64 =
                                                                reader.result.split(
                                                                    ","
                                                                )[1];
                                                            let blobInfo =
                                                                blobCache.create(
                                                                    id,
                                                                    file,
                                                                    base64
                                                                );
                                                            blobCache.add(
                                                                blobInfo
                                                            );

                                                            /* call the callback and populate the Title field with the file name */
                                                            cb(
                                                                blobInfo.blobUri(),
                                                                {
                                                                    title: file.name,
                                                                }
                                                            );
                                                        };
                                                    reader.readAsDataURL(file);
                                                };

                                                input.click();
                                            },
                                        }}
                                        value={content}
                                        onEditorChange={handleEditorChange}
                                    />
                                )}
                            />
                            {errors?.content && (
                                <div className="mt-2 text-xs font-semibold text-red-500">
                                    {errors?.content.message}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between">
                            <a
                                href="/admin/news"
                                className="w-24 px-4 py-2 text-sm font-semibold tracking-wider text-center text-white bg-gray-400 rounded hover:bg-gray-700"
                            >
                                Cancel
                            </a>
                            <button
                                className={`w-24 px-4 py-2 text-sm font-semibold tracking-wider text-white rounded text-center flex justify-center items-center bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed`}
                                disabled={
                                    Object.keys(errors).length !== 0 ||
                                    news.loading
                                }
                            >
                                {news.loading ? (
                                    <Loading color="text-white" size={5} />
                                ) : (
                                    "Publish"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default EditNews;
